/**
 * React Query hook for the Cogni FDA letter AI search path.
 *
 * Wraps ``authApi.fdaLetterAISearch`` (POST ``/fda-letters/search``) with
 * the same caching characteristics as ``useSearchQueryV2Drugs``:
 *   - 5-minute ``staleTime`` (cached results reused on remount).
 *   - 30-minute ``gcTime`` (kept in cache for back-navigation).
 *   - No refetch on window focus.
 *
 * Signature ``(query, enabled)`` is deliberately simpler than the drug
 * hook's ``(query, options, enabled)``: v1 letter search has no
 * filter / region / pagination / top_k overrides.  If a future v2 adds
 * those, the signature can grow to match.
 */
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../services/authApi';
import type { LetterAISearchResponse } from '../types';

/**
 * Optional letter-corpus filters (Task 8).  Snake_case field names mirror
 * the backend ``LetterAISearchRequest`` schema so the hook body is
 * forward-passed to the wire without renaming gymnastics.
 */
export interface LetterAISearchFilters {
  letter_types?: string[];
  letter_corpus?: string[];
  letter_roles?: string[];
  letter_divisions?: string[];
}

export function useSearchQueryV2Letters(
  query: string,
  enabled: boolean = true,
  filters?: LetterAISearchFilters,
) {
  // Stable key fragments for the filter arrays so cache hits don't
  // depend on object identity.  Empty / undefined arrays serialize to
  // ``''`` and behave equivalently in the cache key (a missing array and
  // an empty array are the same "no filter" request to the backend).
  // Sort before joining so toggle order doesn't fragment the cache —
  // backend already sort+dedupes (build_fda_letter_filter_clauses), so
  // ['warning','untitled'] and ['untitled','warning'] produce identical
  // responses and should share a cache entry.
  const ltKey = [...(filters?.letter_types ?? [])].sort().join(',');
  const lcKey = [...(filters?.letter_corpus ?? [])].sort().join(',');
  const lrKey = [...(filters?.letter_roles ?? [])].sort().join(',');
  const ldKey = [...(filters?.letter_divisions ?? [])].sort().join(',');
  return useQuery<LetterAISearchResponse | null>({
    queryKey: ['search-v2-letters', query, ltKey, lcKey, lrKey, ldKey],
    queryFn: async () => {
      if (!query.trim()) return null;
      return authApi.fdaLetterAISearch(query, filters);
    },
    enabled: enabled && query.trim().length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export default useSearchQueryV2Letters;
